<template>
  <div class="sd-card border-l-4 border-l-securdia-blue" style="margin-left: -4rem; margin-right: -4rem;">
    <!-- Header Card -->
    <div class="p-4 border-b border-secondary-100">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-securdia-blue/10 rounded-lg flex items-center justify-center">
            <svg class="w-5 h-5 text-securdia-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
            </svg>
          </div>
          <div>
            <h3 class="text-base font-bold text-secondary-800">
              {{ $t('sales_dashboard.sales_velocity_title') }}
            </h3>
            <p class="text-xs text-secondary-500">
              {{ $t('sales_dashboard.sales_velocity_description') }}
            </p>
          </div>
        </div>
        <button 
          @click="resetToDefaults"
          class="px-3 py-1.5 text-xs font-medium border border-secondary-200 rounded-md hover:bg-secondary-50 flex items-center gap-1.5 text-secondary-600"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
          </svg>
          {{ $t('sales_dashboard.reset_btn') }}
        </button>
      </div>
    </div>

    <div class="p-4">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <!-- Input Parameters -->
        <div class="space-y-4">
          <h4 class="font-semibold text-secondary-700 flex items-center gap-2 text-sm">
            <svg class="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
            </svg>
                        {{ $t('sales_dashboard.input_parameters') }}
          </h4>

          <!-- Opportunities -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-secondary-700">
                                {{ $t('sales_dashboard.opportunities_count') }}
              </label>
              <input 
                type="number" 
                v-model.number="opportunities"
                min="0"
                max="10000"
                class="w-24 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-blue focus:border-securdia-blue"
              >
            </div>
            <input 
              type="range" 
              v-model.number="opportunities"
              min="0" 
              max="500" 
              step="1"
              class="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-securdia-blue"
            >
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.opportunities_help') }}</p>
          </div>

          <!-- Average Deal Value -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-secondary-700">
                                {{ $t('sales_dashboard.avg_deal_value') }} (kr)
              </label>
              <input 
                type="number" 
                v-model.number="avgDealValue"
                min="0"
                step="1000"
                class="w-28 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-blue focus:border-securdia-blue"
              >
            </div>
            <input 
              type="range" 
              v-model.number="avgDealValue"
              min="0" 
              max="500000" 
              step="1000"
              class="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-securdia-orange"
            >
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.avg_deal_value_help', { value: formatCompact(defaults.avgDealValue) }) }}</p>
          </div>

          <!-- Win Rate -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-secondary-700">
                {{ $t('sales_dashboard.win_rate_label') }}
              </label>
              <input 
                type="number" 
                v-model.number="winRate"
                min="0"
                max="100"
                class="w-20 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-blue focus:border-securdia-blue"
              >
            </div>
            <input 
              type="range" 
              v-model.number="winRate"
              min="0" 
              max="100" 
              step="1"
              class="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-green-500"
            >
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.win_rate_help') }}</p>
          </div>

          <!-- Sales Cycle Length -->
          <div class="space-y-2">
            <div class="flex items-center justify-between">
              <label class="text-sm font-medium text-secondary-700">
                {{ $t('sales_dashboard.sales_cycle_label') }}
              </label>
              <input 
                type="number" 
                v-model.number="cycleLength"
                min="1"
                max="365"
                class="w-20 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-blue focus:border-securdia-blue"
              >
            </div>
            <input 
              type="range" 
              v-model.number="cycleLength"
              min="1" 
              max="180" 
              step="1"
              class="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer accent-purple-500"
            >
            <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.sales_cycle_help') }}</p>
          </div>
        </div>

        <!-- Results Column -->
        <div class="space-y-4">
          <!-- Main Velocity Display - KPI Card Style -->
          <div class="bg-white rounded-lg shadow-sm p-4" style="border-left: 4px solid #003366;">
            <div class="text-center">
              <p class="text-sm font-medium text-securdia-blue">{{ $t('sales_dashboard.your_sales_velocity') }}</p>
              <div class="text-3xl font-bold text-secondary-800 mt-1">{{ formatCompact(dailyVelocity) }}</div>
              <p class="text-xs text-securdia-blue mt-1">{{ $t('sales_dashboard.per_day') }}</p>
            </div>
            
            <div class="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-secondary-200">
              <div class="text-center">
                <div class="text-lg font-bold text-secondary-800">{{ formatCompact(monthlyVelocity) }}</div>
                <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.per_month') }}</p>
              </div>
              <div class="text-center">
                <div class="text-lg font-bold text-secondary-800">{{ formatCompact(yearlyVelocity) }}</div>
                <p class="text-xs text-secondary-500">{{ $t('sales_dashboard.per_year_forecast') }}</p>
              </div>
            </div>
          </div>

          <!-- Formula explanation -->
          <div class="p-3 bg-secondary-50 rounded-lg border border-secondary-100">
            <h5 class="font-medium text-secondary-700 mb-2 text-xs uppercase tracking-wide">{{ $t('sales_dashboard.velocity_formula') }}</h5>
            <p class="text-xs text-secondary-600 font-mono bg-white p-2 rounded border border-secondary-200">
              Velocity = ({{ opportunities }} × {{ formatCompact(avgDealValue) }} × {{ winRate }}%) / {{ cycleLength }} {{ $t('sales_dashboard.days') }}
            </p>
            <p class="text-xs text-secondary-500 mt-2">
              {{ $t('sales_dashboard.velocity_explanation') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Scenario Comparison Section -->
      <div class="mt-6 pt-4 border-t border-secondary-100">
        <h4 class="font-semibold text-secondary-700 mb-3 flex items-center gap-2 text-sm">
          <svg class="w-4 h-4 text-securdia-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
                    {{ $t('sales_dashboard.scenario_comparison') }}
        </h4>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <!-- Improved scenario inputs -->
          <div class="p-3 bg-orange-50/50 rounded-lg border border-orange-100">
            <h5 class="font-medium text-secondary-700 mb-3 text-xs uppercase tracking-wide">{{ $t('sales_dashboard.improved_scenario') }}</h5>
            
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <label class="text-sm text-secondary-600">{{ $t('sales_dashboard.new_win_rate') }}</label>
                <input 
                  type="number" 
                  v-model.number="improvedWinRate"
                  min="0"
                  max="100"
                  class="w-20 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-orange focus:border-securdia-orange"
                >
              </div>
              
              <div class="flex items-center justify-between">
                <label class="text-sm text-secondary-600">{{ $t('sales_dashboard.new_sales_cycle') }}</label>
                <input 
                  type="number" 
                  v-model.number="improvedCycleLength"
                  min="1"
                  max="365"
                  class="w-20 px-2 py-1 text-right text-sm border border-secondary-300 rounded font-mono focus:ring-2 focus:ring-securdia-orange focus:border-securdia-orange"
                >
              </div>
            </div>
          </div>

          <!-- Comparison results -->
          <div class="p-3 bg-secondary-50/50 rounded-lg border border-secondary-100">
            <h5 class="font-medium text-secondary-700 mb-3 text-xs uppercase tracking-wide">{{ $t('sales_dashboard.comparison') }}</h5>
            
            <div class="grid grid-cols-2 gap-3">
              <!-- Current -->
              <div class="text-center p-2 bg-white rounded-lg border border-secondary-200">
                <p class="text-xs text-secondary-500 mb-1">{{ $t('sales_dashboard.current') }}</p>
                <p class="text-sm font-bold text-secondary-700">{{ formatCompact(dailyVelocity) }}/{{ $t('sales_dashboard.day') }}</p>
                <p class="text-xs text-secondary-500">{{ formatCompact(yearlyVelocity) }}/{{ $t('sales_dashboard.year') }}</p>
              </div>
              
              <!-- Improved -->
              <div class="text-center p-2 bg-green-50 rounded-lg border border-green-200">
                <p class="text-xs text-green-600 mb-1">{{ $t('sales_dashboard.improved') }}</p>
                <p class="text-sm font-bold text-green-700">{{ formatCompact(improvedDailyVelocity) }}/{{ $t('sales_dashboard.day') }}</p>
                <p class="text-xs text-green-600">{{ formatCompact(improvedYearlyVelocity) }}/{{ $t('sales_dashboard.year') }}</p>
              </div>
            </div>

            <!-- Improvement summary -->
            <div v-if="improvementPercent > 0" class="mt-3 p-2 bg-green-100 rounded-lg text-center">
              <p class="text-xs text-green-700 font-medium">{{ $t('sales_dashboard.potential_improvement') }}</p>
              <p class="text-lg font-bold text-green-700">+{{ improvementPercent.toFixed(0) }}%</p>
              <p class="text-xs text-green-600">= {{ formatCompact(improvedYearlyVelocity - yearlyVelocity) }} {{ $t('sales_dashboard.more_per_year') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SalesVelocityCalculator',
  props: {
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      opportunities: 50,
      avgDealValue: 50000,
      winRate: 25,
      cycleLength: 30,
      // Improved scenario values
      improvedWinRate: 35,
      improvedCycleLength: 23
    };
  },
  computed: {
    defaults() {
      const last90Days = new Date();
      last90Days.setDate(last90Days.getDate() - 90);
      
      const recentContracts = this.contracts.filter(c => {
        const date = new Date(c.created_at || c.start_date);
        return date >= last90Days;
      });
      
      const opportunities = recentContracts.length || 50;
      const avgDealValue = recentContracts.length > 0
        ? recentContracts.reduce((sum, c) => sum + this.getContractValue(c), 0) / recentContracts.length
        : 50000;
      
      return {
        opportunities,
        avgDealValue: Math.round(avgDealValue),
        winRate: 25,
        cycleLength: 30
      };
    },
    
    // Sales Velocity = (Opportunities × Avg Value × Win Rate) / Sales Cycle
    dailyVelocity() {
      if (this.cycleLength <= 0) return 0;
      return (this.opportunities * this.avgDealValue * (this.winRate / 100)) / this.cycleLength;
    },
    
    monthlyVelocity() {
      return this.dailyVelocity * 30;
    },
    
    yearlyVelocity() {
      return this.dailyVelocity * 365;
    },
    
    // Improved scenario calculations
    improvedDailyVelocity() {
      if (this.improvedCycleLength <= 0) return 0;
      return (this.opportunities * this.avgDealValue * (this.improvedWinRate / 100)) / this.improvedCycleLength;
    },
    
    improvedYearlyVelocity() {
      return this.improvedDailyVelocity * 365;
    },
    
    improvementPercent() {
      if (this.dailyVelocity <= 0) return 0;
      return ((this.improvedDailyVelocity - this.dailyVelocity) / this.dailyVelocity) * 100;
    }
  },
  watch: {
    // Update improved scenario when current values change
    winRate(newVal) {
      if (this.improvedWinRate < newVal) {
        this.improvedWinRate = Math.min(100, newVal + 10);
      }
    },
    cycleLength(newVal) {
      if (this.improvedCycleLength > newVal) {
        this.improvedCycleLength = Math.max(1, newVal - 7);
      }
    }
  },
  mounted() {
    this.resetToDefaults();
  },
  methods: {
    resetToDefaults() {
      this.opportunities = this.defaults.opportunities;
      this.avgDealValue = this.defaults.avgDealValue;
      this.winRate = this.defaults.winRate;
      this.cycleLength = this.defaults.cycleLength;
      // Set improved scenario defaults
      this.improvedWinRate = Math.min(100, this.winRate + 10);
      this.improvedCycleLength = Math.max(1, this.cycleLength - 7);
    },
    
    getContractValue(contract) {
      const rentalCost = this.parseMoneyValue(contract.rental_month_cost);
      const cashSum = this.parseMoneyValue(contract.cash_sum);
      const period = contract.contract_period || 36;
      
      if (contract.agreement_type === 'cash' || cashSum > 0) {
        return cashSum;
      }
      return rentalCost * period;
    },
    
    parseMoneyValue(value) {
      if (!value) return 0;
      if (typeof value === 'number') return value;
      return parseFloat(String(value).replace(/[^\d.-]/g, '')) || 0;
    },
    
    formatCompact(value) {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M kr`;
      }
      if (value >= 1000) {
        return `${Math.round(value / 1000)}k kr`;
      }
      return `${Math.round(value)} kr`;
    },
    
    formatCurrency(value) {
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  }
};
</script>

<style scoped>
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: currentColor;
  cursor: pointer;
}
</style>
